import { Link } from 'react-router-dom';
import { Building2, FileText, Users } from 'lucide-react';
import Preloader from '@/components/Preloader';
import PassosDaFolha from './PassosDaFolha';
import { useConferencia, useFolha } from './useFolha';
import { competenciaPorExtenso, reais } from './tipos';

/**
 * O primeiro passo: o que o sistema leu da folha do escritório, setor por
 * setor, com os totais que o próprio relatório imprime.
 */
export default function FolhaPage() {
  const { folhaId, data: folha, isLoading, isError } = useFolha();
  const { data: conferencia } = useConferencia(folhaId, Boolean(folha));

  if (isLoading) {
    return <Preloader telaCheia />;
  }

  if (isError || !folha) {
    return <p className="px-lg py-lg text-[14px] text-danger">Não foi possível abrir esta competência.</p>;
  }

  return (
    <div className="px-lg py-lg">
      <header className="mb-lg">
        <h1 className="text-[22px] font-bold text-ink">Folha de {competenciaPorExtenso(folha.competencia)}</h1>
        <p className="mt-xs flex flex-wrap items-center gap-md text-[13px] text-muted">
          <span className="flex items-center gap-xs">
            <FileText size={14} />
            {folha.arquivo_nome ?? 'PDF do escritório'}
          </span>
          <span className="flex items-center gap-xs">
            <Users size={14} />
            {folha.empresa.funcionarios} funcionários
          </span>
          <span className="flex items-center gap-xs">
            <Building2 size={14} />
            {folha.setores.length} setores
          </span>
        </p>
      </header>

      <PassosDaFolha folha={folha} conferencia={conferencia} atual="folha" />

      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <table className="w-full text-[13px]">
          <thead className="border-b-2 border-border bg-surface-subtle text-[11px] tracking-wide text-ink-soft uppercase">
            <tr>
              <th className="px-sm py-sm text-left font-bold">Setor da folha</th>
              <th className="px-sm py-sm text-left font-bold">Centro de custo no ERP</th>
              <th className="px-sm py-sm text-right font-bold">Funcionários</th>
              <th className="px-sm py-sm text-right font-bold">Proventos</th>
              <th className="px-sm py-sm text-right font-bold">Descontos</th>
              <th className="px-sm py-sm text-right font-bold">Líquido</th>
            </tr>
          </thead>
          <tbody>
            {folha.setores.map((setor) => (
              <tr key={setor.id} className="border-b border-border last:border-0 even:bg-dominant">
                <td className="px-sm py-xs">
                  <span className="font-bold text-ink">{setor.nome}</span>
                  <span className="ml-xs text-[11px] text-muted">{setor.codigo}</span>
                </td>
                <td className="px-sm py-xs">
                  {setor.centro_custo ?? (
                    <Link to="/centros-de-custo" className="font-bold text-warning hover:underline">
                      a vincular em Centros de custo
                    </Link>
                  )}
                </td>
                <td className="px-sm py-xs text-right tabular-nums">{setor.funcionarios}</td>
                <td className="px-sm py-xs text-right tabular-nums">{reais.format(setor.proventos)}</td>
                <td className="px-sm py-xs text-right tabular-nums">{reais.format(setor.descontos)}</td>
                <td className="px-sm py-xs text-right font-bold tabular-nums">{reais.format(setor.liquido)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-border bg-surface-subtle font-bold">
              <td className="px-sm py-sm text-ink" colSpan={2}>
                Resumo geral da empresa
              </td>
              <td className="px-sm py-sm text-right tabular-nums">{folha.empresa.funcionarios}</td>
              <td className="px-sm py-sm text-right tabular-nums">{reais.format(folha.empresa.proventos)}</td>
              <td className="px-sm py-sm text-right tabular-nums">{reais.format(folha.empresa.descontos)}</td>
              <td className="px-sm py-sm text-right tabular-nums">{reais.format(folha.empresa.liquido)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="mt-md text-[12px] text-muted">
        A soma dos setores tem de reproduzir o resumo geral — é a primeira das sete conferências.
      </p>
    </div>
  );
}
